import {
  Body,
  Button,
  Checkbox,
  Chip,
  Heading,
  IconButton,
} from '@assembly-js/design-system';
import { FieldArray, useFormikContext } from 'formik';
import { useState } from 'react';

import {
  AppNotificationConfig,
  AppNotificationSetting,
  NotificationSurface,
} from 'src/constants/dataTypes/appsTypes';
import { ModalWrapper } from 'src/legacy/components/Modals';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';

const SURFACE_META: {
  id: NotificationSurface;
  label: string;
  description: string;
}[] = [
  { id: 'product', label: 'Product', description: 'Notification center.' },
  {
    id: 'email',
    label: 'Email',
    description: 'Email notifications sent to the internal user.',
  },
];

// Leading-dot accent colors for the surface pills.
const SURFACE_DOT_COLOR: Record<NotificationSurface, string> = {
  product: '#4263EB',
  email: '#2F9E44',
};

const DEFAULT_NEW_SURFACES: NotificationSurface[] = ['product', 'email'];

// Only the slice of the form this section reads/writes — the parent Formik schema owns the
// full shape, so we keep this loose and touch notificationConfig (plus the app name) only.
interface NotificationConfigFormValues {
  name?: string;
  notificationConfig?: AppNotificationConfig;
}

type SettingDraft = { label: string; surfaces: NotificationSurface[] };

interface NotificationSettingModalProps {
  // The setting being edited, or null when adding a new one.
  initialSetting: AppNotificationSetting | null;
  onClose: () => void;
  onSave: (draft: SettingDraft) => void;
}

// Modal that authors a single notification setting. It holds local draft state so closing with
// Cancel discards edits; the parent commits the draft to the Formik settings array on save.
function NotificationSettingModal({
  initialSetting,
  onClose,
  onSave,
}: NotificationSettingModalProps) {
  const isEditing = initialSetting != null;
  const [label, setLabel] = useState(initialSetting?.label ?? '');
  const [surfaces, setSurfaces] = useState<NotificationSurface[]>(
    initialSetting?.surfaces ?? DEFAULT_NEW_SURFACES,
  );

  const toggleSurface = (surface: NotificationSurface, checked: boolean) => {
    setSurfaces((prev) =>
      checked
        ? Array.from(new Set([...prev, surface]))
        : prev.filter((value) => value !== surface),
    );
  };

  const canSave = label.trim().length > 0 && surfaces.length > 0;

  return (
    <ModalWrapper
      open
      width={520}
      title={
        isEditing ? 'Edit notification setting' : 'Add notification setting'
      }
      description="This becomes a row on every internal user's notifications page."
      successButtonLabel={isEditing ? 'Save setting' : 'Add setting'}
      cancelButtonLabel="Cancel"
      disableSuccessButton={!canSave}
      onClose={onClose}
      onSuccess={() => onSave({ label: label.trim(), surfaces })}
    >
      <div className="flex flex-col gap-5">
        <div>
          <BaseTypography fontType="13Medium">Label</BaseTypography>
          <Body size="sm" className="text-gray-small mb-2">
            What an internal user sees in the &ldquo;Notify me about&rdquo;
            column. Describe the event, not the app.
          </Body>
          <BaseTextField
            fullWidth
            autoFocus
            name="notificationSettingLabel"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. New task assigned"
            sizeVariant="medium"
            variant="outlined"
          />
        </div>
        <div>
          <BaseTypography fontType="13Medium">Surfaces</BaseTypography>
          <Body size="sm" className="text-gray-small mb-2">
            Channels this setting can be delivered on. Internal users only see
            toggles for the surfaces you enable.
          </Body>
          <div className="flex flex-col gap-2">
            {SURFACE_META.map((surface) => (
              <div
                key={surface.id}
                className="border-[1px] border-solid border-gray-200 rounded-lg px-3 py-2.5"
              >
                <Checkbox
                  label={surface.label}
                  description={surface.description}
                  checked={surfaces.includes(surface.id)}
                  onChange={(e) => toggleSurface(surface.id, e.target.checked)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}

export function NotificationSettingsSection() {
  const { values, handleChange, handleBlur } =
    useFormikContext<NotificationConfigFormValues>();
  const notifications = values.notificationConfig?.notifications ?? [];
  const actionLabel = values.notificationConfig?.actionLabel;
  const appName = values.name?.trim() || 'this app';

  // index === null while adding a new setting; a number while editing an existing one.
  const [editor, setEditor] = useState<{ open: boolean; index: number | null }>(
    {
      open: false,
      index: null,
    },
  );
  const closeEditor = () => setEditor({ open: false, index: null });

  return (
    <>
      <div className="border-0 border-b-[1px] border-solid border-gray-200 py-6 mb-4">
        <Heading size="lg">Configure notifications</Heading>
        <Body size="sm" className="text-gray-small mt-2">
          Allow internal users who have your app installed to configure whether
          they receive the app&apos;s notifications. Each entry below becomes a
          row on every internal user&apos;s notification settings page, where
          they can toggle product and email delivery.
        </Body>
      </div>

      <FieldArray name="notificationConfig.notifications">
        {({ push, replace, remove }) => {
          const handleSave = (draft: SettingDraft) => {
            const nextSetting: AppNotificationSetting = {
              // Preserve an existing setting's id; new settings carry an empty id so the
              // backend API stamps a stable UUID (ids are owned by the backend).
              id: editor.index != null ? notifications[editor.index].id : '',
              label: draft.label,
              surfaces: draft.surfaces,
              // Defaults track supported surfaces: on for each supported channel.
              default: {
                product: draft.surfaces.includes('product'),
                email: draft.surfaces.includes('email'),
              },
            };
            if (editor.index != null) {
              replace(editor.index, nextSetting);
            } else {
              push(nextSetting);
            }
            closeEditor();
          };

          return (
            <div className="mb-6">
              <div className="border-[1px] border-solid border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-start justify-between gap-4 bg-gray-100 px-4 py-3 border-0 border-b-[1px] border-solid border-gray-200">
                  <div>
                    <BaseTypography fontType="13Medium">
                      Notification settings
                    </BaseTypography>
                    <Body size="sm" className="text-gray-small">
                      App workspace name shown beneath each row:{' '}
                      <span className="font-medium text-black-heading">
                        {appName}
                      </span>
                    </Body>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    prefixIcon="Plus"
                    label="Add notification setting"
                    onClick={() => setEditor({ open: true, index: null })}
                    className="whitespace-nowrap"
                  />
                </div>

                {notifications.length === 0 ? (
                  <div className="px-4 py-6">
                    <Body size="sm" className="text-gray-small">
                      No notification settings yet. Add one to let internal
                      users control it.
                    </Body>
                  </div>
                ) : (
                  notifications.map((setting, index) => (
                    <div
                      key={`notification-setting-${index}`}
                      className={`flex items-center justify-between gap-4 px-4 py-3 ${
                        index < notifications.length - 1
                          ? 'border-0 border-b-[1px] border-solid border-gray-200'
                          : ''
                      }`}
                    >
                      <div className="min-w-0">
                        <Body className="text-black-heading truncate">
                          {setting.label}
                        </Body>
                        <Body size="sm" className="text-gray-small truncate">
                          {appName}
                        </Body>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {SURFACE_META.filter((surface) =>
                          setting.surfaces.includes(surface.id),
                        ).map((surface) => (
                          <Chip
                            key={surface.id}
                            label={surface.label}
                            variant="outlined"
                            size="sm"
                            prefixIcon={
                              <div
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                  backgroundColor:
                                    SURFACE_DOT_COLOR[surface.id],
                                }}
                              />
                            }
                          />
                        ))}
                        <IconButton
                          icon="Edit"
                          variant="minimal"
                          label="Edit notification setting"
                          onClick={() => setEditor({ open: true, index })}
                        />
                        <IconButton
                          icon="Trash"
                          variant="minimal"
                          label="Delete notification setting"
                          onClick={() => remove(index)}
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>

              {editor.open && (
                <NotificationSettingModal
                  initialSetting={
                    editor.index != null ? notifications[editor.index] : null
                  }
                  onClose={closeEditor}
                  onSave={handleSave}
                />
              )}
            </div>
          );
        }}
      </FieldArray>

      <div className="mb-2">
        <Body size="sm" className="font-medium text-black-heading">
          Action label
        </Body>
        <Body size="sm" className="text-gray-small mb-3">
          Used by surfaces that summarize pending items as &ldquo;[verb] [count]
          [noun]&rdquo; (e.g. &ldquo;Review 3 documents&rdquo;).
        </Body>
        <div className="flex gap-2">
          <BaseTextField
            className="flex-1"
            fullWidth
            label={<Body size="sm">Verb</Body>}
            name="notificationConfig.actionLabel.verb"
            value={actionLabel?.verb ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            placeholder="Review"
            sizeVariant="medium"
            variant="outlined"
          />
          <BaseTextField
            className="flex-1"
            fullWidth
            label={<Body size="sm">Singular noun</Body>}
            name="notificationConfig.actionLabel.singularNoun"
            value={actionLabel?.singularNoun ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            placeholder="document"
            sizeVariant="medium"
            variant="outlined"
          />
          <BaseTextField
            className="flex-1"
            fullWidth
            label={<Body size="sm">Plural noun</Body>}
            name="notificationConfig.actionLabel.pluralNoun"
            value={actionLabel?.pluralNoun ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            InputLabelProps={{ shrink: true }}
            placeholder="documents"
            sizeVariant="medium"
            variant="outlined"
          />
        </div>
      </div>
    </>
  );
}
